import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { getInitials } from "@/utils/initials-format";
import type { PersonStatus } from "./use-person-profile";

const STATUS_BADGE: Record<
  PersonStatus,
  { label: string; className: string; dotClassName: string }
> = {
  ativo: {
    label: "Ativo",
    className:
      "bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-900",
    dotClassName: "bg-emerald-500",
  },
  ausente: {
    label: "Férias",
    className:
      "bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-900",
    dotClassName: "bg-amber-500",
  },
  inativo: {
    label: "Inativo",
    className:
      "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700",
    dotClassName: "",
  },
};

interface ProfileHeaderProps {
  nome: string;
  status: PersonStatus;
  foto?: string;
}

export function ProfileHeader({ nome, status, foto }: ProfileHeaderProps) {
  const isInativo = status === "inativo";
  const badge = STATUS_BADGE[status];

  return (
    <>
      {/* Banner */}
      <div
        className={cn(
          "relative h-20 w-full transition-colors duration-500 sm:h-28",
          isInativo ? "bg-slate-200/50 dark:bg-slate-800/30" : "bg-slate-100 dark:bg-primary/5",
        )}
      />

      <div className="-mt-10 mb-5 flex items-end justify-between px-4 sm:-mt-12 sm:px-6">
        <div className="group relative">
          <Avatar
            className={cn(
              "h-20 w-20 border-[4px] border-background bg-slate-100 shadow-md transition-transform duration-300 group-hover:scale-105 dark:bg-slate-900 sm:h-24 sm:w-24",
              isInativo && "opacity-80 grayscale",
            )}
          >
            {foto && <AvatarImage src={foto} alt={nome} className="object-cover" />}
            <AvatarFallback className="bg-slate-200 text-2xl font-bold text-slate-700 dark:bg-slate-800 dark:text-slate-300">
              {getInitials(nome)}
            </AvatarFallback>
          </Avatar>
        </div>

        <Badge
          className={cn(
            "rounded-full px-2.5 py-0.5 text-[11px] font-medium shadow-none",
            badge.className,
          )}
        >
          {badge.label}
        </Badge>
      </div>

      <div className="mb-6 px-4 sm:px-6">
        <h2
          className={cn(
            "text-xl font-bold tracking-tight text-foreground sm:text-2xl",
            isInativo && "opacity-60",
          )}
        >
          {nome}
        </h2>
      </div>
    </>
  );
}
